import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/lib/usePermission";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
// Phase K.4 (2026-08-07) — refund UI + credit-memos card.
import AdminRefundModal from "@/components/AdminRefundModal";
import CreditMemosCard from "@/components/CreditMemosCard";
import { pickDefaultBankFromMethodsResponse } from "@/lib/paymentMethod";
import { toast } from "sonner";
import {
    ArrowLeft, FileText, Send, RotateCcw, BadgeDollarSign, Ban, Download,
    Plus, Trash2, ChevronUp, ChevronDown, Save, CreditCard, CheckCircle2,
    AlertTriangle, Copy, Clock, Undo2, Pencil,
} from "lucide-react";
import ViewedChip from "@/components/ViewedChip";
import RefundModal from "@/components/RefundModal";
import PdfPreview from "@/components/PdfPreview";
import InvoiceDetailsEditModal from "@/components/InvoiceDetailsEditModal";

const REINSTATE_REASONS = [
    { code: "voided_in_error", label: "Voided in error" },
    { code: "customer_requested", label: "Customer requested" },
    { code: "billing_correction", label: "Billing correction" },
    { code: "internal_cleanup", label: "Internal cleanup" },
    { code: "other", label: "Other (explain below)" },
];

const VIEW_CHIP_STATUSES = new Set(["sent", "viewed", "overdue"]);

const STATUS_LABEL = {
    draft: "Draft", sent: "Sent", viewed: "Viewed",
    paid: "Paid", overdue: "Overdue", void: "Void",
};
const STATUS_COLOR = {
    draft: "bg-slate-100 text-slate-700",
    sent: "bg-blue-50 text-blue-900",
    viewed: "bg-violet-50 text-violet-800",
    paid: "bg-emerald-100 text-emerald-900",
    overdue: "bg-red-50 text-red-700",
    void: "bg-slate-200 text-slate-700",
};

const LINE_TYPES = [
    { value: "rent", label: "Rent" },
    { value: "late_fee", label: "Late fee" },
    { value: "damage", label: "Damage" },
    { value: "adjustment", label: "Adjustment" },
    { value: "other", label: "Other" },
];

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
    if (!s) return "—";
    return s.slice(0, 10);
}

// Convert raw text in a "$X.XX" or "12.34" input into integer cents.
function parseAmountToCents(raw) {
    if (raw == null || raw === "") return 0;
    const cleaned = String(raw).replace(/[^0-9.\-]/g, "");
    const n = parseFloat(cleaned);
    if (Number.isNaN(n)) return 0;
    return Math.round(n * 100);
}

function dollarsString(cents) {
    if (cents == null || cents === 0) return "";
    return (cents / 100).toFixed(2);
}


// ----------------------------------------------------------------------
// Phase AUDIT-COLLAPSE — the legacy inline audit trail on the invoice
// detail page rendered the raw ``details`` JSON inline, which
// overflowed horizontally whenever an event carried a long
// ``user_agent`` string (e.g. the "Reinstated" event captures the
// browser UA in details). This component:
//   1. Defaults collapsed with a chevron + event-count badge.
//   2. Persists the collapsed state to localStorage per admin.
//   3. Renders a compact one-line summary per row (event name +
//      chip-summary of top 3 metadata keys + right-aligned timestamp).
//   4. Click a row → inline expansion showing the full metadata JSON,
//      user_agent, and IP so all the raw data remains one-click away.
// ----------------------------------------------------------------------

const AUDIT_COLLAPSE_STORAGE_KEY = "admin.invoiceDetail.auditTrailCollapsed";

function _readAuditCollapsed() {
    try {
        const raw = window.localStorage.getItem(AUDIT_COLLAPSE_STORAGE_KEY);
        return raw == null ? true : raw === "1";
    } catch (_) { return true; }
}

function _writeAuditCollapsed(v) {
    try { window.localStorage.setItem(AUDIT_COLLAPSE_STORAGE_KEY, v ? "1" : "0"); }
    catch (_) { /* private mode / quota — silent no-op */ }
}

const _AUDIT_DETAIL_HIDDEN_KEYS = new Set(["user_agent", "ip"]);

function _summarizeDetails(details) {
    if (!details || typeof details !== "object") return null;
    const entries = Object.entries(details).filter(
        ([k, v]) => !_AUDIT_DETAIL_HIDDEN_KEYS.has(k) && v !== null && v !== undefined && v !== "",
    );
    if (entries.length === 0) return null;
    const kept = entries.slice(0, 3).map(([k, v]) => {
        let val = v;
        if (typeof val === "string" && val.length > 40) val = val.slice(0, 40) + "…";
        if (typeof val === "boolean") val = val ? "true" : "false";
        return `${k}: ${val}`;
    });
    if (entries.length > 3) kept.push(`+${entries.length - 3} more`);
    return kept.join(" · ");
}

function AuditTrailCard({ entries }) {
    const [collapsed, setCollapsed] = useState(_readAuditCollapsed);
    const [expandedRow, setExpandedRow] = useState(null);
    const count = entries.length;
    const toggleCollapsed = () => {
        setCollapsed((prev) => {
            const next = !prev;
            _writeAuditCollapsed(next);
            return next;
        });
    };
    const reversed = entries.slice().reverse();
    return (
        <Card>
            <button
                type="button"
                onClick={toggleCollapsed}
                aria-expanded={!collapsed}
                data-testid="invoice-audit-toggle"
                className="w-full flex items-center gap-2 px-6 py-4 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-900/30 rounded-t-md"
            >
                {collapsed
                    ? <ChevronDown size={16} className="text-slate-400 -rotate-90" aria-hidden="true" />
                    : <ChevronDown size={16} className="text-slate-400" aria-hidden="true" />}
                <span className="text-base font-semibold text-slate-900">Audit trail</span>
                <span className="text-xs text-slate-500 ml-1" data-testid="invoice-audit-count">
                    · {count} {count === 1 ? "event" : "events"}
                </span>
            </button>
            {!collapsed && (
                <CardContent className="p-0 border-t border-slate-100">
                    <ol className="divide-y divide-slate-100 text-sm">
                        {reversed.map((a, idx) => {
                            const isOpen = expandedRow === idx;
                            const summary = _summarizeDetails(a.details);
                            return (
                                <li key={idx} data-testid={`invoice-audit-${idx}`}>
                                    <button
                                        type="button"
                                        onClick={() => setExpandedRow(isOpen ? null : idx)}
                                        className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-1 focus-visible:ring-blue-900/20"
                                        aria-expanded={isOpen}
                                        data-testid={`invoice-audit-row-toggle-${idx}`}
                                    >
                                        <span className="font-medium text-slate-900 capitalize flex-shrink-0">{(a.action || "").replace(/_/g, " ")}</span>
                                        {summary && (
                                            <span className="text-xs text-slate-500 truncate flex-1 min-w-0">{summary}</span>
                                        )}
                                        <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0" data-testid={`invoice-audit-ts-${idx}`}>
                                            {fmtDate(a.at)} {(a.at || "").slice(11, 19)}
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="px-4 pb-3 pt-1 bg-slate-50 border-t border-slate-100 text-xs text-slate-700 space-y-2" data-testid={`invoice-audit-details-${idx}`}>
                                            {a.actor && (
                                                <div><span className="font-semibold text-slate-500">Actor:</span> {a.actor}</div>
                                            )}
                                            {a.details?.ip && (
                                                <div><span className="font-semibold text-slate-500">IP:</span> {a.details.ip}</div>
                                            )}
                                            {a.details?.user_agent && (
                                                <div className="break-all"><span className="font-semibold text-slate-500">User agent:</span> {a.details.user_agent}</div>
                                            )}
                                            <div>
                                                <div className="font-semibold text-slate-500 mb-0.5">Raw details</div>
                                                <pre className="whitespace-pre-wrap break-all bg-white border border-slate-200 rounded p-2 text-[11px] leading-snug max-h-64 overflow-auto">{JSON.stringify(a.details ?? {}, null, 2)}</pre>
                                            </div>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                </CardContent>
            )}
        </Card>
    );
}


export default function AdminInvoiceDetailPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [editing, setEditing] = useState(false);
    const [draftItems, setDraftItems] = useState([]);
    const [draftDirty, setDraftDirty] = useState(false);
    // Phase J.2 Pass 3 — refund modal target (selected payment)
    const [refundTarget, setRefundTarget] = useState(null);
    // Phase J.4b (2026-07-29) — Charge ACH now flow state.
    const [chargeAchOpen, setChargeAchOpen] = useState(false);
    const [chargeAchBusy, setChargeAchBusy] = useState(false);
    const [customerBank, setCustomerBank] = useState(null); // {last4, bank_name}
    // Phase N.3 — edit-details modal (issue_date + terms on drafts).
    const [detailsEditOpen, setDetailsEditOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/invoices/${id}`);
            setInv(data);
            setDraftItems((data.line_items || []).map((li) => ({ ...li })));
            setDraftDirty(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    // Phase J.4b — pull the customer's default bank (last4 + name) so
    // the confirm modal can show what will actually be debited. Fires
    // only when we have an invoice with a customer_id.
    useEffect(() => {
        if (!inv?.customer_id) { setCustomerBank(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/customers/${inv.customer_id}/payment-methods`);
                if (cancelled) return;
                // Phase J.4b-FIX (2026-07-31) — see
                // ``lib/paymentMethod.pickDefaultBankFromMethodsResponse``
                // for the contract this reads. Extracted so a unit test can
                // guard the response-shape contract with the backend.
                setCustomerBank(pickDefaultBankFromMethodsResponse(data));
            } catch { setCustomerBank(null); }
        })();
        return () => { cancelled = true; };
    }, [inv?.customer_id]);

    const chargeAchNow = async () => {
        if (chargeAchBusy) return;
        setChargeAchBusy(true);
        try {
            const { data } = await api.post(`/admin/invoices/${id}/charge-ach`);
            toast.success(
                `Charge initiated (${data.payment_intent_id}). Invoice will show "Processing" until settlement.`,
            );
            setChargeAchOpen(false);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setChargeAchBusy(false);
        }
    };

    const loadPreview = async () => {
        try {
            const res = await api.post(`/admin/invoices/${id}/preview-pdf`, null, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            setPdfUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        if (inv) loadPreview();
        return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
        /* eslint-disable-next-line */
    }, [inv?.id, inv?.updated_at]);

    const isDraft = inv?.status === "draft";

    const editorTotalCents = useMemo(
        () => draftItems.reduce((sum, li) => sum + Math.round((parseFloat(li.quantity) || 0) * (li.unit_amount_cents || 0)), 0),
        [draftItems]
    );

    const updateRow = (index, patch) => {
        setDraftItems((prev) => {
            const next = [...prev];
            next[index] = { ...next[index], ...patch };
            return next;
        });
        setDraftDirty(true);
    };

    const addRow = () => {
        setDraftItems((prev) => [...prev, {
            id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            description: "",
            quantity: 1,
            unit_amount_cents: 0,
            line_type: "other",
        }]);
        setDraftDirty(true);
    };

    const removeRow = (index) => {
        setDraftItems((prev) => prev.filter((_, i) => i !== index));
        setDraftDirty(true);
    };

    const moveRow = (index, delta) => {
        const next = [...draftItems];
        const target = index + delta;
        if (target < 0 || target >= next.length) return;
        [next[index], next[target]] = [next[target], next[index]];
        setDraftItems(next);
        setDraftDirty(true);
    };

    const saveDraftItems = async () => {
        for (const li of draftItems) {
            if (!li.description || !li.description.trim()) {
                toast.error("Every line needs a description"); return;
            }
            if (!li.quantity || Number(li.quantity) <= 0) {
                toast.error("Quantity must be greater than zero"); return;
            }
        }
        setBusy(true);
        try {
            const body = {
                line_items: draftItems.map((li) => ({
                    description: li.description.trim(),
                    quantity: Number(li.quantity),
                    unit_amount_cents: Number(li.unit_amount_cents) || 0,
                    line_type: li.line_type || "other",
                })),
            };
            const { data } = await api.patch(`/admin/invoices/${id}`, body);
            setInv(data);
            setDraftItems((data.line_items || []).map((li) => ({ ...li })));
            setDraftDirty(false);
            setEditing(false);
            toast.success("Line items saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const cancelEdit = () => {
        setDraftItems((inv.line_items || []).map((li) => ({ ...li })));
        setDraftDirty(false);
        setEditing(false);
    };

    const doAction = async (path, label) => {
        setBusy(true);
        try {
            await api.post(`/admin/invoices/${id}/${path}`);
            toast.success(`${label} ✓`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const markPaid = async () => {
        const ref = window.prompt("Optional payment reference (e.g. ACH trace #):", "");
        if (ref === null) return;
        setBusy(true);
        try {
            await api.post(`/admin/invoices/${id}/mark-paid`, {
                paid_amount_cents: inv.total_cents,
                payment_method: "ach",
                payment_reference: ref || undefined,
            });
            toast.success("Marked paid ✓");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const voidInvoice = async () => {
        const reason = window.prompt("Reason for voiding (required):");
        if (!reason) return;
        setBusy(true);
        try {
            await api.post(`/admin/invoices/${id}/void`, { reason });
            toast.success("Invoice voided");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Phase REINSTATE — un-void modal state.
    const { ready: permsReady, hasPermission } = usePermission();
    const canReinstate = permsReady && hasPermission("Invoices", "Reinstate");
    const [reinstateOpen, setReinstateOpen] = useState(false);
    const [reinstateReasonCode, setReinstateReasonCode] = useState("");
    const [reinstateReasonText, setReinstateReasonText] = useState("");
    const [reinstateNotify, setReinstateNotify] = useState(false);
    const [reinstateError, setReinstateError] = useState(null);
    const reinstateCanSubmit = (
        reinstateReasonCode &&
        (reinstateReasonCode !== "other" || (reinstateReasonText.trim().length > 0))
    );
    const submitReinstate = async () => {
        setBusy(true);
        setReinstateError(null);
        try {
            const res = await api.post(`/admin/invoices/${id}/reinstate`, {
                reason_code: reinstateReasonCode,
                reason_text: reinstateReasonText.trim() || undefined,
                notify_customer: reinstateNotify,
            });
            toast.success(`Invoice reinstated — status restored to ${res.data.restored_to_status}`);
            if (res.data.legacy_reinstate) {
                toast.info("Original status was unknown, defaulted to 'sent'.");
            }
            setReinstateOpen(false);
            setReinstateReasonCode("");
            setReinstateReasonText("");
            setReinstateNotify(false);
            await load();
        } catch (err) {
            // 409 payloads carry structured detail; surface the credit_note_id
            // path specifically since it's actionable.
            const detail = err?.response?.data?.detail;
            if (detail && typeof detail === "object") {
                if (detail.code === "superseded_by_credit_note") {
                    setReinstateError(
                        `This invoice cannot be reinstated because it has been superseded by credit note #${detail.credit_note_id}. To reinstate, first void or reverse the credit note.`,
                    );
                } else {
                    setReinstateError(detail.message || formatApiError(err));
                }
            } else {
                setReinstateError(formatApiError(err));
            }
        } finally {
            setBusy(false);
        }
    };

    const downloadPdf = () => {
        if (!pdfUrl) return;
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `${inv.invoice_number}.pdf`;
        a.click();
    };

    const canSend = isDraft;
    const canResend = inv && ["sent", "viewed", "overdue"].includes(inv.status);
    const canMarkPaid = inv && !["paid", "void"].includes(inv.status);
    const canVoid = canMarkPaid;
    // Phase K.4 — refund gating.
    const refundableCents = Math.max(
        0, Number(inv?.total_cents || 0) - Number(inv?.refunded_amount_cents || 0),
    );
    const canRefund = inv && ["paid", "partially_refunded"].includes(inv.status) && refundableCents > 0;
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [k4Memos, setK4Memos] = useState([]);

    useEffect(() => {
        if (!inv?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/invoices/${inv.id}/credit-memos`);
                setK4Memos(data.memos || []);
            } catch {
                setK4Memos([]);
            }
        })();
    }, [inv?.id, inv?.refunded_amount_cents, inv?.status]);

    if (loading) return <DashboardShell><p className="text-sm text-slate-500" data-testid="invoice-detail-loading">Loading…</p></DashboardShell>;
    if (!inv) return <DashboardShell><p className="text-sm text-slate-500">Not found.</p></DashboardShell>;

    return (
        <DashboardShell>
            <div className="fade-up flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Button asChild variant="ghost" size="sm" className="-ml-3 gap-1.5">
                        <Link to="/admin/invoices" data-testid="invoice-back-link">
                            <ArrowLeft size={14} /> All invoices
                        </Link>
                    </Button>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-3" data-testid="invoice-detail-number">
                        {inv.invoice_number}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Bill to{" "}
                        {inv.customer_name ? (
                            <Link
                                to={`/admin/customers/${inv.customer_id}`}
                                className="text-blue-900 hover:underline"
                                data-testid="invoice-header-customer-link"
                                title={inv.customer_name}
                            >
                                {inv.customer_name}
                            </Link>
                        ) : (
                            <Link
                                to={`/admin/customers/${inv.customer_id}`}
                                className="text-blue-900 hover:underline"
                                data-testid="invoice-header-customer-link"
                            >
                                customer profile
                            </Link>
                        )}
                        {inv.rental_id && (
                            <>
                                {" "}· from rental{" "}
                                <Link
                                    to={`/admin/rentals/${inv.rental_id}`}
                                    className="text-blue-900 hover:underline"
                                    data-testid="invoice-header-rental-link"
                                >
                                    {inv.rental_unit_type && inv.rental_unit_number
                                        ? `${inv.rental_unit_type} #${inv.rental_unit_number}`
                                        : inv.rental_unit_number
                                            ? `#${inv.rental_unit_number}`
                                            : `${inv.rental_id.slice(0, 8)}…`}
                                </Link>
                            </>
                        )}
                    </p>
                </div>
                <div className="text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_COLOR[inv.status]}`} data-testid="invoice-status-pill">
                            {STATUS_LABEL[inv.status]}
                        </span>
                        {VIEW_CHIP_STATUSES.has(inv.status) && inv.last_viewed_at && (
                            <ViewedChip at={inv.last_viewed_at} by={inv.last_viewed_by_email} />
                        )}
                        {inv.statement_id && (
                            <Link
                                to={`/admin/statements/${inv.statement_id}`}
                                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
                                data-testid="invoice-statement-badge"
                                title="This invoice is part of a consolidated statement"
                            >
                                Part of Statement {inv.statement_id.slice(0, 8)}…
                            </Link>
                        )}
                    </div>
                    <p className="text-3xl font-display font-bold tracking-tight text-slate-900 mt-3 tabular-nums" data-testid="invoice-total-display">
                        {fmtMoney(editing ? editorTotalCents : inv.total_cents)}
                    </p>
                    <p className="text-xs text-slate-500">Due {fmtDate(inv.due_date)}</p>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="invoice-actions">
                {canSend && (
                    <Button onClick={() => doAction("send", "Sent")} disabled={busy || editing} className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="invoice-send-btn">
                        <Send size={14} /> Send
                    </Button>
                )}
                {canResend && (
                    <Button onClick={() => doAction("resend", "Resent")} disabled={busy} variant="outline" className="gap-2" data-testid="invoice-resend-btn">
                        <RotateCcw size={14} /> Resend
                    </Button>
                )}
                {canMarkPaid && (
                    <Button onClick={markPaid} disabled={busy || editing} variant="outline" className="gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="invoice-mark-paid-btn">
                        <BadgeDollarSign size={14} /> Mark paid
                    </Button>
                )}
                {/* Phase J.4b — Charge ACH now. Visible when invoice is
                    sent/overdue AND the customer has a verified bank. */}
                {inv && ["sent", "overdue"].includes(inv.status) && customerBank && (
                    <Button
                        onClick={() => setChargeAchOpen(true)}
                        disabled={busy || editing}
                        variant="outline"
                        className="gap-2 text-blue-900 border-blue-200 hover:bg-blue-50"
                        data-testid="invoice-charge-ach-btn"
                    >
                        <BadgeDollarSign size={14} /> Charge ACH now
                    </Button>
                )}
                {canVoid && (
                    <Button onClick={voidInvoice} disabled={busy || editing} variant="outline" className="gap-2 text-red-600 border-red-200 hover:bg-red-50" data-testid="invoice-void-btn">
                        <Ban size={14} /> Void
                    </Button>
                )}
                {inv?.status === "void" && canReinstate && (
                    <Button onClick={() => setReinstateOpen(true)} disabled={busy} variant="outline" className="gap-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="invoice-reinstate-btn">
                        <Undo2 size={14} /> Reinstate invoice
                    </Button>
                )}
                <Button onClick={downloadPdf} disabled={!pdfUrl} variant="outline" className="gap-2" data-testid="invoice-download-btn">
                    <Download size={14} /> Download PDF
                </Button>
                {/* Phase K.4 (2026-08-07) — Refund button. */}
                {canRefund && (
                    <Button
                        onClick={() => setShowRefundModal(true)}
                        disabled={busy}
                        variant="outline"
                        className="gap-2 text-blue-900 border-blue-200 hover:bg-blue-50"
                        data-testid="invoice-refund-btn"
                    >
                        <Undo2 size={14} /> Refund
                    </Button>
                )}
            </div>

            {/* Phase K.4 (2026-08-07) — Credit memos card. */}
            <div className="fade-up">
                <CreditMemosCard
                    memos={k4Memos}
                    paidCents={inv?.total_cents}
                    refundedCents={inv?.refunded_amount_cents}
                    heading="Credit memos (admin view)"
                />
            </div>

            {/* Phase K.4 (2026-08-07) — Refund modal. */}
            {inv && (
                <AdminRefundModal
                    kind="invoice"
                    resource={inv}
                    open={showRefundModal}
                    onOpenChange={setShowRefundModal}
                    onRefunded={() => load && load()}
                />
            )}

            {/* Phase J.4b (2026-07-29) — Charge ACH now confirm modal. */}
            <Dialog open={chargeAchOpen} onOpenChange={setChargeAchOpen}>
                <DialogContent data-testid="charge-ach-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">
                            Charge ACH for {inv?.invoice_number}?
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                            <div><span className="font-medium">Amount:</span> {inv && fmtMoney((inv.total_cents || 0) - (inv.amount_paid_cents || 0))}</div>
                            <div><span className="font-medium">Bank:</span> {customerBank?.bank_name} &middot;&middot;&bull;&bull; {customerBank?.last4}</div>
                            <div><span className="font-medium">Settlement:</span> 3&ndash;4 business days</div>
                        </div>
                        <p className="text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3 text-xs leading-relaxed">
                            This will initiate a <strong>live ACH debit</strong> against the customer&apos;s bank
                            ending in <strong>&bull;&bull;{customerBank?.last4}</strong>. Funds typically settle in
                            3&ndash;4 business days. This action <strong>cannot be reversed</strong> until
                            settlement &mdash; a refund will be needed afterwards.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setChargeAchOpen(false)} disabled={chargeAchBusy} data-testid="charge-ach-cancel">
                            Cancel
                        </Button>
                        <Button onClick={chargeAchNow} disabled={chargeAchBusy} className="bg-blue-900 hover:bg-blue-800" data-testid="charge-ach-confirm">
                            {chargeAchBusy ? "Charging…" : "Confirm charge"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>


            {/* Phase REINSTATE — confirmation modal */}
            <Dialog open={reinstateOpen} onOpenChange={(open) => { setReinstateOpen(open); if (!open) setReinstateError(null); }}>
                <DialogContent data-testid="reinstate-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">Reinstate invoice {inv?.invoice_number}?</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 text-sm">
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-700">
                            <div><span className="font-medium">Current status:</span> Void</div>
                            <div><span className="font-medium">Amount:</span> {inv && fmtMoney(inv.total_cents)}</div>
                            <div><span className="font-medium">Voided:</span> {inv?.voided_at ? fmtDate(inv.voided_at.slice(0, 10)) : "unknown date"}</div>
                            <div><span className="font-medium">Void reason:</span> {inv?.voided_reason || "—"}</div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Reason (required)</label>
                            <Select value={reinstateReasonCode} onValueChange={setReinstateReasonCode}>
                                <SelectTrigger data-testid="reinstate-reason-select"><SelectValue placeholder="Choose a reason…" /></SelectTrigger>
                                <SelectContent>
                                    {REINSTATE_REASONS.map((r) => (
                                        <SelectItem key={r.code} value={r.code} data-testid={`reinstate-reason-${r.code}`}>{r.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Additional context {reinstateReasonCode === "other" ? "(required)" : "(optional)"}
                            </label>
                            <Textarea
                                value={reinstateReasonText}
                                onChange={(e) => setReinstateReasonText(e.target.value.slice(0, 500))}
                                placeholder="Add any context that will help future audits…"
                                data-testid="reinstate-reason-text"
                                rows={3}
                            />
                            <div className="text-[10px] text-slate-400 mt-1 text-right">{reinstateReasonText.length}/500</div>
                        </div>
                        <label className="flex items-start gap-2 cursor-pointer">
                            <Checkbox
                                checked={reinstateNotify}
                                onCheckedChange={(v) => setReinstateNotify(!!v)}
                                data-testid="reinstate-notify-checkbox"
                            />
                            <span className="text-sm text-slate-700">Notify customer via email</span>
                        </label>
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                            <div><strong>Auto-charge will NOT be re-enabled.</strong> If this invoice was previously in an auto-charge schedule, you&apos;ll need to manually re-enroll it.</div>
                            <div><strong>Credit notes remain.</strong> Any credit notes issued during voiding stay in place. Accounting must be reconciled manually if needed.</div>
                        </div>
                        {reinstateError && (
                            <div className="rounded-md border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="reinstate-error-banner">
                                {reinstateError}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setReinstateOpen(false)} data-testid="reinstate-cancel-btn">Cancel</Button>
                        <Button
                            onClick={submitReinstate}
                            disabled={busy || !reinstateCanSubmit}
                            className="bg-emerald-700 hover:bg-emerald-800"
                            data-testid="reinstate-confirm-btn"
                        >
                            Reinstate invoice
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <FileText size={16} /> Invoice details
                            </CardTitle>
                            {isDraft && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setDetailsEditOpen(true)}
                                    className="gap-1.5"
                                    data-testid="edit-invoice-details-btn"
                                >
                                    <Pencil size={14} /> Edit
                                </Button>
                            )}
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Issue date</p>
                                <p className="text-slate-900 font-medium">{fmtDate(inv.issue_date)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Terms</p>
                                <p className="text-slate-900 font-medium">{inv.terms}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Subtotal</p>
                                <p className="text-slate-900 font-medium tabular-nums">{fmtMoney(editing ? editorTotalCents : inv.subtotal_cents)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Total</p>
                                <p className="text-slate-900 font-semibold tabular-nums">{fmtMoney(editing ? editorTotalCents : inv.total_cents)}</p>
                            </div>
                            {inv.last_sent_at && (
                                <div className="col-span-2 text-xs text-slate-500">
                                    Last sent {fmtDate(inv.last_sent_at)} to <span className="font-mono">{inv.sent_to_email}</span>
                                </div>
                            )}
                            {inv.paid_at && (
                                <div className="col-span-2 text-xs text-emerald-700">
                                    Paid {fmtDate(inv.paid_at)} · {inv.payment_method?.toUpperCase()} · ref {inv.payment_reference_masked || "—"}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-base font-semibold">Line items</CardTitle>
                            {isDraft && !editing && (
                                <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="gap-1.5"
                                        data-testid="invoice-edit-lines">
                                    Edit line items
                                </Button>
                            )}
                            {isDraft && editing && (
                                <div className="flex gap-2">
                                    <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={busy}
                                            data-testid="invoice-cancel-edit">
                                        Cancel
                                    </Button>
                                    <Button size="sm" onClick={saveDraftItems} disabled={busy || !draftDirty}
                                            className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                                            data-testid="invoice-save-lines">
                                        <Save size={14} /> Save
                                    </Button>
                                </div>
                            )}
                        </CardHeader>
                        <CardContent className="p-0">
                            {!editing && (
                                <table className="min-w-full text-sm" data-testid="invoice-line-items-readonly">
                                    <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                        <tr>
                                            <th className="text-left py-2 px-4">Description</th>
                                            <th className="text-right py-2 px-4">Qty</th>
                                            <th className="text-right py-2 px-4">Unit</th>
                                            <th className="text-right py-2 px-4">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {(inv.line_items || []).map((li) => (
                                            <tr key={li.id} data-testid={`invoice-line-${li.id}`}>
                                                <td className="py-2 px-4">
                                                    <div className="text-slate-900">{li.description}</div>
                                                    <div className="text-xs text-slate-500 capitalize">{(li.line_type || "other").replace("_", " ")}</div>
                                                </td>
                                                <td className="py-2 px-4 text-right tabular-nums text-slate-700">{li.quantity}</td>
                                                <td className="py-2 px-4 text-right tabular-nums text-slate-700">{fmtMoney(li.unit_amount_cents)}</td>
                                                <td className="py-2 px-4 text-right tabular-nums font-semibold text-slate-900">{fmtMoney(li.total_cents)}</td>
                                            </tr>
                                        ))}
                                        {(inv.line_items || []).length === 0 && (
                                            <tr><td colSpan={4} className="py-6 px-4 text-center text-sm text-slate-400 italic">No line items yet.</td></tr>
                                        )}
                                    </tbody>
                                    <tfoot>
                                        <tr className="border-t border-slate-200">
                                            <td colSpan={3} className="py-3 px-4 text-right font-semibold text-slate-500">Total</td>
                                            <td className="py-3 px-4 text-right font-bold text-blue-900 tabular-nums text-lg">{fmtMoney(inv.total_cents)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            )}

                            {editing && (
                                <div className="p-4 space-y-3" data-testid="invoice-line-items-editor">
                                    {draftItems.map((li, idx) => {
                                        const rowTotal = Math.round((parseFloat(li.quantity) || 0) * (li.unit_amount_cents || 0));
                                        return (
                                            <div key={li.id} className="grid grid-cols-12 gap-2 items-start rounded-md border border-slate-200 bg-slate-50/40 p-2.5"
                                                 data-testid={`invoice-edit-row-${idx}`}>
                                                <div className="col-span-5">
                                                    <Input
                                                        value={li.description}
                                                        onChange={(e) => updateRow(idx, { description: e.target.value })}
                                                        placeholder="Description"
                                                        data-testid={`invoice-edit-description-${idx}`}
                                                    />
                                                    <Select value={li.line_type || "other"}
                                                            onValueChange={(v) => updateRow(idx, { line_type: v })}>
                                                        <SelectTrigger className="mt-1.5 h-8 text-xs" data-testid={`invoice-edit-type-${idx}`}>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {LINE_TYPES.map((t) => (
                                                                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="col-span-2">
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        value={li.quantity}
                                                        onChange={(e) => updateRow(idx, { quantity: e.target.value })}
                                                        className="text-right tabular-nums"
                                                        placeholder="Qty"
                                                        data-testid={`invoice-edit-qty-${idx}`}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <div className="relative">
                                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                                        <Input
                                                            type="text"
                                                            value={dollarsString(li.unit_amount_cents)}
                                                            onChange={(e) => updateRow(idx, { unit_amount_cents: parseAmountToCents(e.target.value) })}
                                                            className="pl-5 text-right tabular-nums"
                                                            placeholder="0.00"
                                                            data-testid={`invoice-edit-unit-${idx}`}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-span-2 text-right pt-2 pr-2 font-semibold text-slate-900 tabular-nums"
                                                     data-testid={`invoice-edit-row-total-${idx}`}>
                                                    {fmtMoney(rowTotal)}
                                                </div>
                                                <div className="col-span-1 flex flex-col gap-1 items-end">
                                                    <Button size="icon" variant="ghost" className="h-7 w-7"
                                                            onClick={() => moveRow(idx, -1)} disabled={idx === 0}
                                                            data-testid={`invoice-edit-up-${idx}`}>
                                                        <ChevronUp size={14} />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" className="h-7 w-7"
                                                            onClick={() => moveRow(idx, 1)} disabled={idx === draftItems.length - 1}
                                                            data-testid={`invoice-edit-down-${idx}`}>
                                                        <ChevronDown size={14} />
                                                    </Button>
                                                    <Button size="icon" variant="ghost"
                                                            className="h-7 w-7 text-red-600 hover:bg-red-50"
                                                            onClick={() => removeRow(idx)}
                                                            data-testid={`invoice-edit-remove-${idx}`}>
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    <Button variant="outline" size="sm" onClick={addRow} className="gap-1.5"
                                            data-testid="invoice-edit-add-row">
                                        <Plus size={14} /> Add line item
                                    </Button>
                                    <div className="border-t pt-3 flex justify-between items-center">
                                        <span className="text-sm text-slate-500">Editor total</span>
                                        <span className="text-2xl font-display font-bold text-blue-900 tabular-nums"
                                              data-testid="invoice-edit-total">
                                            {fmtMoney(editorTotalCents)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {(inv.notes || inv.customer_notes) && (
                        <Card>
                            <CardHeader><CardTitle className="text-base font-semibold">Notes</CardTitle></CardHeader>
                            <CardContent className="text-sm space-y-3">
                                {inv.customer_notes && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Customer note (visible on PDF)</p>
                                        <p className="text-slate-700 whitespace-pre-line">{inv.customer_notes}</p>
                                    </div>
                                )}
                                {inv.notes && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Internal note (admin-only)</p>
                                        <p className="text-slate-500 whitespace-pre-line">{inv.notes}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Phase J.2 Pass 3 — Payments timeline */}
                    {Array.isArray(inv.payments) && inv.payments.length > 0 && (
                        <Card id="payments" data-testid="admin-invoice-payments-section">
                            <CardHeader>
                                <CardTitle className="text-base font-semibold flex items-center gap-2">
                                    <CreditCard size={16} /> Payments &amp; refunds
                                    <span className="text-xs font-normal text-slate-500">
                                        ({inv.payments.length} attempt{inv.payments.length === 1 ? "" : "s"})
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-4 space-y-3">
                                {inv.payments.map((p) => {
                                    const isSucceeded = p.status === "succeeded";
                                    const isRefundable = (p.status === "succeeded" || p.status === "partially_refunded")
                                        && (p.refundable_cents || 0) > 0;
                                    const statusColor = (
                                        p.status === "succeeded" ? "bg-emerald-100 text-emerald-900 ring-emerald-200" :
                                        p.status === "refunded" || p.status === "partially_refunded" ? "bg-purple-100 text-purple-900 ring-purple-200" :
                                        p.status === "failed" || p.status === "canceled" ? "bg-red-50 text-red-700 ring-red-200" :
                                        p.status === "processing" ? "bg-amber-50 text-amber-900 ring-amber-200" :
                                        "bg-slate-100 text-slate-700 ring-slate-200"
                                    );
                                    return (
                                        <div key={p.id} className="rounded-md ring-1 ring-slate-200 bg-white"
                                             data-testid={`admin-invoice-payment-card-${p.id}`}>
                                            <div className="px-3 py-2 flex flex-wrap items-start justify-between gap-2 border-b border-slate-100">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${statusColor}`}>
                                                        {p.status.replace("_", " ")}
                                                    </span>
                                                    {p.consumed && (
                                                        <span className="text-[10px] uppercase tracking-wider text-slate-400 ring-1 ring-slate-200 rounded-full px-1.5 py-0.5">
                                                            consumed
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => { navigator.clipboard.writeText(p.stripe_payment_intent_id || ""); toast.success("Copied"); }}
                                                        className="font-mono text-xs text-slate-700 hover:text-blue-900 inline-flex items-center gap-1"
                                                        title={p.stripe_payment_intent_id}
                                                    >
                                                        {p.stripe_payment_intent_id_masked} <Copy size={10} />
                                                    </button>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-semibold text-slate-900 tabular-nums">{fmtMoney(p.amount_cents)}</div>
                                                    {p.refunded_cents_total > 0 && (
                                                        <div className="text-xs text-purple-700">
                                                            refunded {fmtMoney(p.refunded_cents_total)}
                                                        </div>
                                                    )}
                                                    <div className="text-xs text-slate-500">{p.created_at?.slice(0, 16).replace("T", " ")}</div>
                                                </div>
                                            </div>

                                            {p.last_payment_error_message && (
                                                <div className="px-3 py-2 text-sm text-red-700 bg-red-50/40 flex items-start gap-2">
                                                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                                                    <span>{p.last_payment_error_message}</span>
                                                </div>
                                            )}

                                            {/* Webhook events sub-timeline */}
                                            {Array.isArray(p.webhook_events) && p.webhook_events.length > 0 && (
                                                <ol className="px-3 py-2 text-xs space-y-1.5"
                                                    data-testid={`admin-invoice-payment-events-${p.id}`}>
                                                    {p.webhook_events.map((ev, idx) => (
                                                        <li key={idx} className="flex items-center gap-2 text-slate-600">
                                                            {ev.outcome?.handled
                                                                ? <CheckCircle2 size={11} className="text-emerald-600" />
                                                                : <Clock size={11} className="text-slate-400" />}
                                                            <span className="font-medium text-slate-700">{ev.event_type}</span>
                                                            <span className="font-mono text-[10px] text-slate-400">{ev.event_id_masked}</span>
                                                            {ev.outcome?.duplicate && <span className="text-[10px] text-amber-700">replay</span>}
                                                            <span className="text-[10px] text-slate-400 ml-auto">
                                                                {ev.received_at?.slice(11, 19)}
                                                            </span>
                                                        </li>
                                                    ))}
                                                </ol>
                                            )}

                                            {/* Refunds attached to this payment */}
                                            {Array.isArray(p.refunds) && p.refunds.length > 0 && (
                                                <div className="px-3 py-2 border-t border-slate-100 bg-purple-50/30 space-y-1"
                                                     data-testid={`admin-invoice-payment-refunds-${p.id}`}>
                                                    {p.refunds.map((r) => (
                                                        <div key={r.id} className="text-xs flex items-center justify-between gap-2">
                                                            <div className="flex items-center gap-2">
                                                                <RotateCcw size={11} className="text-purple-700" />
                                                                <span className="font-mono text-[10px]">{r.stripe_refund_id?.slice(-12)}</span>
                                                                <span className="capitalize">{(r.reason || "").replace("_", " ")}</span>
                                                                <span className={
                                                                    r.status === "succeeded" ? "text-emerald-700"
                                                                    : r.status === "failed" ? "text-red-700"
                                                                    : "text-amber-700"
                                                                }>{r.status}</span>
                                                                {r.admin_email && <span className="text-slate-400">by {r.admin_email}</span>}
                                                            </div>
                                                            <span className="font-semibold tabular-nums text-purple-900">
                                                                {fmtMoney(r.amount_cents)}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Refund CTA (super-admin only, succeeded/partial only) */}
                                            {isRefundable && user?.role === "super_admin" && (
                                                <div className="px-3 py-2 border-t border-slate-100 flex justify-end">
                                                    <Button
                                                        size="sm" variant="outline"
                                                        onClick={() => setRefundTarget(p)}
                                                        className="gap-1.5 border-purple-300 text-purple-900 hover:bg-purple-50"
                                                        data-testid={`admin-invoice-payment-refund-btn-${p.id}`}
                                                    >
                                                        <RotateCcw size={12} />
                                                        Refund {fmtMoney(p.refundable_cents)} remaining
                                                    </Button>
                                                </div>
                                            )}
                                            {isSucceeded && !isRefundable && p.refunded_cents_total > 0 && (
                                                <div className="px-3 py-1.5 text-xs text-slate-400 text-right">Fully refunded</div>
                                            )}
                                        </div>
                                    );
                                })}
                            </CardContent>
                        </Card>
                    )}

                    {(inv.audit_trail || []).length > 0 && (
                        <AuditTrailCard entries={inv.audit_trail} />
                    )}
                </div>

                <div className="lg:col-span-1">
                    <Card className="sticky top-4">
                        <CardHeader>
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <FileText size={16} /> PDF preview
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-2">
                            {pdfUrl ? (
                                <PdfPreview
                                    src={pdfUrl}
                                    downloadHref={pdfUrl}
                                    downloadName={inv?.invoice_number ? `${inv.invoice_number}.pdf` : "invoice.pdf"}
                                    testid="invoice-pdf-preview"
                                />
                            ) : (
                                <p className="text-sm text-slate-500 p-6 text-center">Generating preview…</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Phase J.2 Pass 3 — Refund modal */}
            <RefundModal
                payment={refundTarget}
                open={!!refundTarget}
                onOpenChange={(o) => !o && setRefundTarget(null)}
                onIssued={() => { setRefundTarget(null); load(); }}
            />

            {/* Phase N.3 — Edit invoice details (draft only) */}
            <InvoiceDetailsEditModal
                open={detailsEditOpen}
                invoice={inv}
                onOpenChange={setDetailsEditOpen}
                onSaved={(updated) => {
                    setInv((prev) => ({ ...prev, ...updated }));
                }}
            />
        </DashboardShell>
    );
}
